import mixin from './mixin';
import { userStore } from './store/userStore.js';
import vodItem from './components/VODitem.vue';
import discountComponent from './components/DiscountComponent.vue';
import subscriptionComponent from './components/SubscriptionComponent.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const swalWithBulmaButtons = Swal.mixin({
    customClass: {
        confirmButton: "button is-secondary is-rounded is-small mr-2",
        cancelButton: "button is-secondary is-outlined is-rounded is-small ml-2"
    },
    buttonsStyling: false
});

const delay = ms => new Promise(res => setTimeout(res, ms));

const accountApp = {
    setup() {
        return { v$: useVuelidate() }
    },
    mixins: [mixin],
    components: { vodItem, discountComponent, subscriptionComponent },
    data() {
        return {
            userApi: '',
            logoutApi: '',
            purchasesApi: '',
            favouritesApi: '',
            passwordApi: '',
            subscriptionsInfoApi: '',
            memberInfoApi: '',
            userState: {},
            tabs: [],
            tabContent: [],
            profileActive: true,
            purchaseActive: false,
            favouritesActive: false,
            redirect: '',
            favourites: [],
            favouritesError: false,
            purchases: [],
            purchasesError: false,
            changePassword: false,
            requestPassword: false,
            requestPending: false,
            changeEmail: false,
            loadFavourites: false,
            loadPurchases: false,
            loadSubscriptions: false,
            editAccount: false,
            allPlans: [],
            plans: [],
            plansError: false,
            addDiscount: false,
            saveInfoBusy: false,
            removeSubscriptionApi: '',
            removeSubscriptionTitle: '',
            removeSubscriptionText: '',
            removeSubscriptionCancel: '',
            removeSubscriptionConfirm: '',
            generalErrorTxt: 'Er is iets misgegaan, probeer het later opnieuw.',
            culture: 'nl-NL'
        }
    },
    validations() {
        return {
            username: { required, email }
        }
    },
    computed: {
        user() {
            return userStore.user;
        },
        loggedIn() {
            return userStore.loggedIn;
        }
    },
    methods: {
        async init() {
            let self = this;
            await delay(500);
            if (!this.loggedIn) {
                location.href = this.redirect;
            }
            window.addEventListener('memberChanged', async (event) => {
                userStore.user = event.detail.user;
                if (self.favourites.length) {
                    if (self.user.favouriteVodItems.length !== self.favourites.length) {
                        self.loadFavourites = true;
                        const favouritesResponse = await self.fetchApiData(self.favouritesApi, '', 'get');
                        if (favouritesResponse && favouritesResponse.status === 200) {
                            self.favourites = Array.from(
                                new Map(favouritesResponse.data.map(item => [item.id, item])).values()
                            );
                        } else {
                            self.favouritesError = true;
                        }
                        self.loadFavourites = false;
                    }
                }
            });
            this.loadSubscriptions = true;
            const plansResponse = await this.fetchApiData(this.subscriptionsInfoApi, '', 'get');
            if (plansResponse && plansResponse.status === 200) {
                this.allPlans = plansResponse.data;
                if (this.user.hasSubscription) {
                    this.plans = this.allPlans.filter(plan => plan.id === this.user.activeSubscription.subscriptionId);
                } else {
                    this.plans = this.allPlans;
                }
            } else {
                this.plansError = true;
            }

            if (window.location.hash) {
                this.toggleTab(window.location.hash.replace('#', ''));
            }
            this.loadSubscriptions = false;
        },
        async toggleTab(tab) {
            switch (tab) {
                case 'profile':
                    this.profileActive = true;
                    this.purchaseActive = false;
                    this.favouritesActive = false;
                    window.history.replaceState('', '/', window.location.pathname)
                    break;
                case 'purchase':
                    if (this.user.purchasedVodItems.length !== this.purchases.length) {
                        this.loadPurchases = true;
                        const purchaseResponse = await this.fetchApiData(this.purchasesApi, '', 'get');
                        if (purchaseResponse && purchaseResponse.status === 200) {
                            this.purchases = purchaseResponse.data;
                        } else {
                            this.purchasesError = true;
                        }
                        this.loadPurchases = false;
                    }
                    this.profileActive = false;
                    this.purchaseActive = true;
                    this.favouritesActive = false;
                    window.location.hash = 'purchase';
                    break;
                case 'favourites':
                    if (this.user.favouriteVodItems.length !== this.favourites.length) {
                        this.loadFavourites = true;
                        const favouritesResponse = await this.fetchApiData(this.favouritesApi, '', 'get');
                        if (favouritesResponse && favouritesResponse.status === 200) {
                            this.favourites = Array.from(
                                new Map(favouritesResponse.data.map(item => [item.id, item])).values()
                            );
                        } else {
                            this.favouritesError = true;
                        }
                        this.loadFavourites = false;
                        window.location.hash = 'favourites';
                    }
                    this.profileActive = false;
                    this.purchaseActive = false;
                    this.favouritesActive = true;
                    break;
                default:
                    this.profileActive = true;
                    this.purchaseActive = false;
                    this.favouritesActive = false;
                    window.history.replaceState('', '/', window.location.pathname)
            }
        },
        async requestNewPassword() {
            this.requestPending = true;
            let pwurl = this.passwordApi;
            const response = await this.fetchApiData(pwurl, '', 'get');
            if (response.status === 200 && response.data) {
                this.requestPending = false;
                this.requestPassword = true;
            }
        },
        async deleteFavourite(vodId) {
            this.loadFavourites = true;
            let url = this.favouritesApi + vodId;
            const response = await this.fetchApiData(url, '', 'delete');
            if (response.status === 200 && response.data) {
                this.favourites = this.favourites.filter((f) => {
                    return f.id !== vodId;
                });
                this.user.favouriteVodItems = this.favourites.map(fav => fav.id);
                userStore.updateUser(this.user);
                this.loadFavourites = false;
            }
        },
        async logout() {
            const logoutResponse = await this.fetchApiData(this.logoutApi, '', 'get');
            if (!logoutResponse || logoutResponse.status != 200) return;
            if (logoutResponse.data) {
                userStore.deleteUser();
                location.href = '/';
            }
        },
        async saveInfo() {
            this.saveInfoBusy = true;
            let data = {
                Firstname: this.user.name.givenname,
                Insertion: '',
                Lastname: this.user.name.surname,
                Telephone: this.user.phone
            }
            const infoResponse = await this.fetchApiData(this.memberInfoApi, data, 'post');
            if (infoResponse && infoResponse.status === 200) {
                userStore.updateUser(this.user);
                this.saveInfoBusy = false;
                this.editAccount = false;
            }
        },
        async stopSubscription() {
            let self = this;
            let title = this.removeSubscriptionTitle.replace('{subscription}', this.plans[0].title);
            swalWithBulmaButtons.fire({
                scrollbarPadding: false,
                title: title,
                text: self.removeSubscriptionText,
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: self.removeSubscriptionCancel,
                confirmButtonText: self.removeSubscriptionConfirm
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const removeSubsriptionResponse = await this.fetchApiData(this.removeSubscriptionApi, '', 'delete');
                    if (removeSubsriptionResponse && removeSubsriptionResponse.status === 200) {
                        if (removeSubsriptionResponse.data && removeSubsriptionResponse.data.typeAlias == 'Success') {
                            const memberResponse = await this.fetchApiData(this.userApi, '', 'get');
                            if (memberResponse && memberResponse.data.id !== null && memberResponse.status === 200) {
                                userStore.updateUser(memberResponse.data);
                            } else {
                                Swal.fire({
                                    title: 'Error!',
                                    text: this.generalErrorTxt,
                                    icon: 'error'
                                });
                            }
                        } else {
                            Swal.fire({
                                title: 'Error!',
                                text: this.generalErrorTxt,
                                icon: 'error'
                            });
                        }
                    } else {
                        Swal.fire({
                            title: 'Error!',
                            text: this.generalErrorTxt,
                            icon: 'error'
                        });
                    }
                }
            });
        },
        toFullDate(dateString) {
            return new Date(dateString).toLocaleDateString(this.culture, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).toLowerCase();
        }
    },
    mounted() {
        if (this.$el.parentNode.dataset.userapi) this.userApi = this.$el.parentNode.dataset.userapi;
        if (this.$el.parentNode.dataset.logout) this.logoutApi = this.$el.parentNode.dataset.logout;
        if (this.$el.parentNode.dataset.passwordapi) this.passwordApi = this.$el.parentNode.dataset.passwordapi;
        if (this.$el.parentNode.dataset.favouritesapi) this.favouritesApi = this.$el.parentNode.dataset.favouritesapi;
        if (this.$el.parentNode.dataset.purchasesapi) this.purchasesApi = this.$el.parentNode.dataset.purchasesapi;
        if (this.$el.parentNode.dataset.redirect) this.redirect = this.$el.parentNode.dataset.redirect;
        if (this.$el.parentNode.dataset.subscriptioninfoapi) this.subscriptionsInfoApi = this.$el.parentNode.dataset.subscriptioninfoapi;
        if (this.$el.parentNode.dataset.memberinfoapi) this.memberInfoApi = this.$el.parentNode.dataset.memberinfoapi;
        if (this.$el.parentNode.dataset.removesubscriptionapi) this.removeSubscriptionApi = this.$el.parentNode.dataset.removesubscriptionapi;
        if (this.$el.parentNode.dataset.removesubscriptiontitle) this.removeSubscriptionTitle = this.$el.parentNode.dataset.removesubscriptiontitle;
        if (this.$el.parentNode.dataset.removesubscriptiontext) this.removeSubscriptionText = this.$el.parentNode.dataset.removesubscriptiontext;
        if (this.$el.parentNode.dataset.removesubscriptioncancel) this.removeSubscriptionCancel = this.$el.parentNode.dataset.removesubscriptioncancel;
        if (this.$el.parentNode.dataset.removesubscriptionconfirm) this.removeSubscriptionConfirm = this.$el.parentNode.dataset.removesubscriptionconfirm;
        if (this.$el.parentNode.dataset.generalerror) this.generalErrorTxt = this.$el.parentNode.dataset.generalerror;
        if (this.$el.parentNode.dataset.culturecode) this.culture = this.$el.parentNode.dataset.culturecode;
        this.init();
    }
}

export default accountApp;